/** SUNTRONIX ONE — ONE POST. Universal transaction entry + ONE Day Book.
 *  Post once — ONE routes it to the right place (Bank / Cash / Card) and it
 *  shows up in the unified Day Book. No duplicate entry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Zap, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, CreditCard, Landmark,
  Wallet, Loader2, BookOpen, Calendar, TrendingUp, TrendingDown, ChevronRight,
} from "lucide-react";

const fmt = (n, cur = "INR") => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(n);
const today = () => new Date().toISOString().slice(0, 10);

const METHOD_ICON = { BANK: Landmark, CASH: Wallet, CARD: CreditCard };

const KIND_CARDS = [
  { id: "RECEIPT", label: "Receipt", hint: "Money in", tone: "emerald", icon: ArrowDownCircle },
  { id: "PAYMENT", label: "Payment", hint: "Money out", tone: "rose", icon: ArrowUpCircle },
  { id: "EXPENSE", label: "Expense", hint: "Money out", tone: "amber", icon: ArrowUpCircle },
  { id: "INCOME", label: "Income", hint: "Money in", tone: "emerald", icon: ArrowDownCircle },
  { id: "TRANSFER", label: "Transfer", hint: "Between accounts", tone: "sky", icon: ArrowLeftRight },
  { id: "CARD_SPEND", label: "Card spend", hint: "Adds to card bill", tone: "violet", icon: CreditCard },
  { id: "CARD_BILL_PAYMENT", label: "Pay card bill", hint: "Clear outstanding", tone: "slate", icon: CreditCard },
];

const TONES = {
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
  rose: "border-rose-300 bg-rose-50 text-rose-700",
  amber: "border-amber-300 bg-amber-50 text-amber-700",
  sky: "border-sky-300 bg-sky-50 text-sky-700",
  violet: "border-violet-300 bg-violet-50 text-violet-700",
  slate: "border-slate-400 bg-slate-100 text-slate-800",
};

export default function OnePost() {
  const [tab, setTab] = useState("post");
  const [access, setAccess] = useState(null);

  useEffect(() => { api.get("/one-post/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ can_access: false })); }, []);

  if (access && !access.can_access) {
    return (
      <div className="p-6" data-testid="one-post-denied">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">
          ONE POST is restricted to Finance administrators.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="one-post-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><Zap className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>ONE POST</h1>
            <p className="text-[13px] text-slate-500">Post once — ONE puts it everywhere. Bank, cash, card & the Day Book, in sync.</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1.5 mb-5 border-b border-slate-200">
        {[["post", "Post a transaction", Zap], ["daybook", "ONE Day Book", BookOpen]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`one-post-tab-${k}`}
            className={"px-3.5 h-9 text-[13px] font-medium -mb-px border-b-2 inline-flex items-center gap-1.5 transition-colors " +
              (tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {tab === "post" ? <PostView /> : <DayBookView />}
    </div>
  );
}

function PostView() {
  const [methods, setMethods] = useState([]);
  const [config, setConfig] = useState({ categories: [], modes: [] });
  const [f, setF] = useState({
    txn_kind: "RECEIPT", method_kind: "", method_id: "", to_method_id: "",
    date: today(), amount: "", party: "", particulars: "", category: "",
    mode: "", reference: "", notes: "", merchant: "",
  });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const amtRef = useRef(null);

  const loadMethods = useCallback(() => {
    api.get("/one-post/methods?scope=BUSINESS").then(({ data }) => setMethods(data.methods || []))
      .catch((e) => { if (e.response?.status === 422) toast.info("Select an establishment in the top bar first."); });
  }, []);
  useEffect(() => { loadMethods(); api.get("/one-post/config").then(({ data }) => setConfig(data)).catch(() => {}); }, [loadMethods]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const isTransfer = f.txn_kind === "TRANSFER";
  const isCard = f.txn_kind === "CARD_SPEND" || f.txn_kind === "CARD_BILL_PAYMENT";

  // The methods valid for the chosen kind.
  const eligible = useMemo(() => {
    if (isCard) return methods.filter((m) => m.method_kind === "CARD");
    if (isTransfer) return methods.filter((m) => m.method_kind === "BANK");
    return methods.filter((m) => m.method_kind !== "CARD"); // bank + cash
  }, [methods, isCard, isTransfer]);

  // Auto-clear method if it no longer belongs to the eligible set.
  useEffect(() => {
    if (f.method_id && !eligible.some((m) => m.method_id === f.method_id)) {
      setF((s) => ({ ...s, method_id: "", method_kind: "", to_method_id: "" }));
    }
  }, [eligible]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickMethod = (m) => setF((s) => ({ ...s, method_id: m.method_id, method_kind: m.method_kind }));

  const submit = async (confirm = false) => {
    if (!f.method_id) return toast.error(isCard ? "Select a card" : "Select an account");
    if (!f.amount || Number(f.amount) <= 0) return toast.error("Enter an amount");
    if (isTransfer && !f.to_method_id) return toast.error("Select a destination account");
    setBusy(true);
    try {
      const payload = {
        scope: "BUSINESS", txn_kind: f.txn_kind, method_kind: f.method_kind,
        method_id: f.method_id, to_method_kind: isTransfer ? "BANK" : null,
        to_method_id: isTransfer ? f.to_method_id : null, date: f.date,
        amount: Number(f.amount), party: f.party || null, particulars: f.particulars || null,
        category: f.category || null, mode: f.mode || null, reference: f.reference || null,
        notes: f.notes || null, merchant: f.merchant || null,
      };
      const { data } = await api.post(`/one-post/post${confirm ? "?confirm_duplicate=true" : ""}`, payload);
      if (data.needs_confirmation) { setDup(data); setBusy(false); return; }
      toast.success(data.message || "Posted ✓");
      setDup(null);
      setF((s) => ({ ...s, amount: "", party: "", particulars: "", reference: "", notes: "", merchant: "" }));
      setTimeout(() => amtRef.current?.focus(), 100);
      loadMethods();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  const Method = ({ m }) => {
    const Icon = METHOD_ICON[m.method_kind] || Landmark;
    const active = f.method_id === m.method_id;
    return (
      <button type="button" onClick={() => pickMethod(m)} data-testid={`method-${m.method_id}`}
        className={"text-left rounded-xl border p-3 transition-colors " +
          (active ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:border-slate-300")}>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <span className="font-semibold text-[13px] text-slate-800 truncate">{m.label}</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 truncate">{m.sublabel}</div>
        <div className="text-[14px] font-bold text-slate-900 mt-1 tabular-nums">
          {m.method_kind === "CARD"
            ? <span className="text-rose-700">{fmt(m.outstanding, m.currency)} <span className="text-[10px] font-medium text-slate-400">outstanding</span></span>
            : fmt(m.balance, m.currency)}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-5" data-testid="one-post-post-view">
      {/* Transaction kind */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">What are you posting?</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="one-post-kinds">
          {KIND_CARDS.map((k) => {
            const active = f.txn_kind === k.id;
            const Icon = k.icon;
            return (
              <button key={k.id} type="button" onClick={() => upd("txn_kind", k.id)} data-testid={`kind-${k.id}`}
                className={"rounded-xl border p-2.5 text-center transition-colors " +
                  (active ? TONES[k.tone] + " ring-1 ring-current" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}>
                <Icon className="h-4 w-4 mx-auto mb-1" />
                <div className="text-[12px] font-semibold leading-tight">{k.label}</div>
                <div className="text-[9px] opacity-70 leading-tight">{k.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Method picker */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
          {isCard ? "Which card?" : isTransfer ? "From which bank account?" : "Post to / from"}
        </div>
        {eligible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-5 text-center text-[13px] text-slate-500" data-testid="no-methods">
            No {isCard ? "cards" : "accounts"} available. Add one in Cash Control, Card Control or ONE Ledger first.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="one-post-methods">
            {eligible.map((m) => <Method key={m.method_id} m={m} />)}
          </div>
        )}
      </div>

      {/* Transfer destination */}
      {isTransfer && f.method_id && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">To which bank account?</div>
          <select value={f.to_method_id} onChange={(e) => upd("to_method_id", e.target.value)} data-testid="one-post-transfer-to"
            className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-[13px]">
            <option value="">Select destination account…</option>
            {eligible.filter((m) => m.method_id !== f.method_id).map((m) => <option key={m.method_id} value={m.method_id}>{m.label}</option>)}
          </select>
        </div>
      )}

      {/* Details */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-2 gap-3">
        <Field label="Amount">
          <Input ref={amtRef} type="number" value={f.amount} onChange={(e) => upd("amount", e.target.value)}
            data-testid="one-post-amount" placeholder="0.00" onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
        </Field>
        <Field label="Date"><Input type="date" value={f.date} onChange={(e) => upd("date", e.target.value)} data-testid="one-post-date" /></Field>
        {f.txn_kind === "CARD_SPEND" ? (
          <Field label="Merchant"><Input value={f.merchant} onChange={(e) => upd("merchant", e.target.value)} data-testid="one-post-merchant" placeholder="e.g. Amazon" /></Field>
        ) : !isTransfer && (
          <Field label={f.txn_kind === "RECEIPT" || f.txn_kind === "INCOME" ? "Received from" : "Paid to"}>
            <Input value={f.party} onChange={(e) => upd("party", e.target.value)} data-testid="one-post-party" placeholder="Name (optional)" />
          </Field>
        )}
        {!isCard && !isTransfer && (
          <Field label="Category">
            <select value={f.category} onChange={(e) => upd("category", e.target.value)} data-testid="one-post-category"
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-[13px]">
              <option value="">Optional</option>
              {(config.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        )}
        <Field label={f.txn_kind === "CARD_SPEND" ? "Purpose / notes" : "Description / particulars"} full={isCard || isTransfer}>
          <Input value={f.particulars} onChange={(e) => upd("particulars", e.target.value)} data-testid="one-post-particulars"
            placeholder={isTransfer ? "e.g. Move funds to savings" : "e.g. Paid electricity bill"} />
        </Field>
        <Field label="Reference / UTR"><Input value={f.reference} onChange={(e) => upd("reference", e.target.value)} data-testid="one-post-reference" placeholder="Optional" /></Field>
      </div>

      {dup && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="one-post-dup">
          {dup.message} Post anyway?
          <div className="mt-2 flex gap-2">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => submit(true)} data-testid="one-post-dup-confirm">Post anyway</Button>
            <Button size="sm" variant="outline" onClick={() => setDup(null)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button disabled={busy} onClick={() => submit()} data-testid="one-post-submit" className="bg-slate-900 hover:bg-slate-800 h-11 px-6">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Zap className="h-4 w-4 mr-1.5" />Post transaction</>}
        </Button>
      </div>
    </div>
  );
}

function DayBookView() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/one-post/day-book?scope=BUSINESS&date=${date}`)
      .then(({ data }) => setData(data))
      .catch((e) => { setData(null); if (e.response?.status === 422) toast.info("Select an establishment in the top bar first."); })
      .finally(() => setLoading(false));
  }, [date]);

  return (
    <div className="space-y-5" data-testid="one-post-daybook-view">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-slate-400" />
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daybook-date" className="w-44" />
        <span className="text-[12px] text-slate-400">Unified daily position across bank, cash & card payments.</span>
      </div>

      {loading && <div className="text-slate-400 py-6 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <DayStat testid="daybook-opening" label="Opening" value={fmt(data.opening)} />
            <DayStat testid="daybook-receipts" label="Receipts" value={fmt(data.receipts)} tone="text-emerald-700" icon={TrendingUp} />
            <DayStat testid="daybook-payments" label="Payments" value={fmt(data.payments)} tone="text-rose-700" icon={TrendingDown} />
            <DayStat testid="daybook-transfers" label="Transfers" value={fmt(data.transfers_out)} tone="text-sky-700" icon={ArrowLeftRight} />
            <DayStat testid="daybook-closing" label="Closing" value={fmt(data.closing)} highlight />
          </div>

          {data.card_spend_today > 0 && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-[12px] text-violet-800 flex items-center gap-2" data-testid="daybook-card-spend">
              <CreditCard className="h-4 w-4" /> {fmt(data.card_spend_today)} spent on cards today — added to card outstanding (not counted in the cash payments above).
            </div>
          )}

          <div>
            <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Account movements</h3>
            <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="daybook-accounts-table">
                <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  {["Account", "Opening", "Receipts", "Payments", "Closing"].map((h, i) => (
                    <th key={i} className={"px-3 py-2 font-semibold " + (i > 0 ? "text-right" : "")}>{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.accounts.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No movement on this day.</td></tr>}
                  {data.accounts.map((a) => (
                    <tr key={a.account_id} className="hover:bg-slate-50" data-testid={`daybook-acct-${a.account_id}`}>
                      <td className="px-3 py-2 text-slate-800">{a.name}<span className="text-[10px] text-slate-400 ml-1.5">{a.type}</span></td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmt(a.opening)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{a.receipts ? fmt(a.receipts) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">{a.payments ? fmt(a.payments) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{fmt(a.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Transactions on {data.date}</h3>
            <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="daybook-txns">
              {data.transactions.length === 0 ? (
                <div className="px-4 py-8 text-center text-[13px] text-slate-400">No transactions recorded for this day.</div>
              ) : data.transactions.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`daybook-txn-${t.id}`}>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                  <span className="flex-1 truncate text-[13px] text-slate-800">
                    {t.particulars}{t.party ? <span className="text-slate-400"> · {t.party}</span> : null}
                    {t.type === "TRANSFER" ? <span className="ml-1 text-[10px] text-sky-600 font-semibold">TRANSFER</span> : null}
                    {t.mode ? <span className="ml-1 text-[10px] text-slate-400">{t.mode}</span> : null}
                  </span>
                  <span className={"text-[13px] font-semibold tabular-nums " + (t.debit ? "text-rose-700" : "text-emerald-700")}>
                    {t.debit ? "− " + fmt(t.debit) : "+ " + fmt(t.credit)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DayStat({ label, value, tone = "text-slate-900", highlight, testid, icon: Icon }) {
  return (
    <div className={"rounded-xl border p-4 " + (highlight ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white")} data-testid={testid}>
      <div className={"text-[11px] font-medium flex items-center gap-1 " + (highlight ? "text-slate-300" : "text-slate-500")}>
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className={"text-[18px] font-bold mt-1 tabular-nums " + (highlight ? "text-white" : tone)}>{value}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return <label className={"space-y-1 " + (full ? "col-span-2" : "")}><span className="text-[11px] font-medium text-slate-500">{label}</span>{children}</label>;
}
